<template>
  <Card
    v-if="teaserData"
    class="navigation-teaser"
    :class="`navigation-teaser--${teaserData.theme || 'dark'} navigation-teaser--${
      teaserData.customClass
    } navigation-teaser--columns-${columnNumber}`"
    tag="nuxt-link"
    :href="link"
    :heading="heading"
    :subtitle="subtitle"
    :label="label"
    :background="background"
  >
    <template #default>
      <PricesContainer
        v-if="teaserData.offer?.prices && !teaserData.noPrice"
        :prices-data="teaserData.offer.prices || []"
        show-highlight
        variant="small"
        :theme="teaserData.theme || 'dark'"
      />
    </template>

    <template #action>
      <LinkButton
        tag="nuxt-link"
        :to="link"
        :class="`btn--${teaserData.link.theme || 'primary'}`"
      >
        {{ teaserData.link?.label }}
      </LinkButton>
    </template>
  </Card>
</template>

<script lang="ts" setup>
import { Card, LinkButton } from '@i22team/td-longtail-vue-components';
import type {
  HeadingElementProps,
  ParagraphElementProps,
  PictureElementProps,
} from '@i22team/td-longtail-vue-components';
import PricesContainer from '~/components/product-detail/prices-container.vue';
import type { NavigationTeaserWithOffer } from '~/stores/navigation';

export type NavigationTeaserProps = {
  teaserData?: NavigationTeaserWithOffer;
  columnNumber?: 'one' | 'two' | 'three';
};

const props = withDefaults(defineProps<NavigationTeaserProps>(), {
  teaserData: undefined,
  columnNumber: 'one',
});

const link = computed(
  () => props.teaserData?.link?.path || `/geraete/${props.teaserData?.offer?.product?.slug}`
);
const label = computed(() => {
  if (!props.teaserData?.label) return undefined;
  return {
    customClass: 'text--bold',
    text: props.teaserData.label,
    noMargin: true,
  } as ParagraphElementProps;
});

const heading = computed(() => {
  if (!props.teaserData?.heading) return undefined;
  return {
    tag: 'p',
    size: props.columnNumber === 'two' ? 'sm' : 'md',
    text: props.teaserData.heading,
    textUppercase: true,
  } as HeadingElementProps;
});
const subtitle = computed(() => {
  if (!props.teaserData?.subheading) return undefined;
  return {
    text: props.teaserData.subheading,
    noMargin: true,
  } as ParagraphElementProps;
});

const background = computed(() => {
  if (!props.teaserData?.image) return undefined;
  const img: PictureElementProps = {
    lazy: true,
    src: props.teaserData.image,
    imageStyle: `object-position: ${props.teaserData.imagePosition || 'center center'}`,
    alt: props.teaserData.alt || '',
  };

  return img;
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.navigation-teaser {
  height: 377px;
  width: 350px;
  --card-container-height: 100%;

  @include mq($mq-lg) {
    width: 456px;
  }

  &--light.card {
    color: var(--color-secondary-fixed);
  }
  :deep(.card__content) {
    display: flex;
    align-items: flex-end;
  }

  :deep(.prices) {
    padding-bottom: 9px;
  }

  :deep(.price__amount--dark) {
    color: var(--color-font);
  }

  &--teufel {
    :deep(.price__amount--highlight) {
      color: var(--color-primary);
    }
  }

  &--columns {
    &-two {
      width: 350px;
    }
  }
}
</style>
